import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { Radio } from "@portal/BaseComponents/Radio/AntRadio";
import { RadioChangeEvent } from "@portal/BaseComponents/Radio/types";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import {
  CustomFieldBetweenValues,
  CustomFieldFilterValues,
  CustomFieldOperatorType,
  CustomFiledBetweenOperands,
  CustomFiledOperands,
} from "@portal/types/entities/custom-field";
import { useState } from "react";
import { FilterFooter } from "../../../Footer/FilterFooter";
import { NumericCustomRange, OnCustomRangeChange } from "./NumericCustomRange";
import { NumericSingleValues } from "./NumericSingleValues";

const t = {
  greaterThan: "Greater than or equals",
  lessThan: "Less than or equals",
  from: "From",
  to: "To",
  unassigned: "Is unassigned",
  equalTo: "Equals",
  Between: "Between",
};

export type CustomFieldRangeValueType<T extends CustomFieldOperatorType> = {
  value: CustomFiledOperands<T>["value"];
  type: T;
  include_unassigned: boolean;
};

interface NumericCustomFieldProps {
  value?: CustomFieldRangeValueType<CustomFieldOperatorType>;
  allowDecimal: boolean;
  onCancel: () => void;
  onSubmit: (
    value: Omit<CustomFieldFilterValues<CustomFieldOperatorType>, "id">
  ) => void;
}

type NumericSelectedValue = Partial<
  CustomFieldRangeValueType<CustomFieldOperatorType>
>;
function NumericCustomField(props: NumericCustomFieldProps) {
  const { value, allowDecimal, onCancel, onSubmit } = props;
  const [selectedValue, setSelectedValue] = useState<
    NumericSelectedValue | undefined
  >(value);
  const [isDisable, setIsDisable] = useState(false);

  function handleUnassignedChange(event: RadioChangeEvent) {
    setSelectedValue({
      value: [],
      type: "IN",
      include_unassigned: true,
    });
    setIsDisable(false);
  }

  function handleCustomRangeChange(...args: OnCustomRangeChange) {
    const [value, status] = args;
    setSelectedValue({
      value,
      type: "BTWN",
      include_unassigned: false,
    });
    setIsDisable(status === "error");
  }

  function handleSubmit() {
    if (selectedValue?.type === "BTWN") {
      const values: Omit<CustomFieldFilterValues<"BTWN">, "id"> = {
        operator: {
          type: "BTWN",
          operands: {
            type: "DEPRECATED_ABSOLUTE",
            value: selectedValue.value as CustomFieldBetweenValues,
          },
        },
        include_unassigned: false,
      };
      onSubmit(values);
    } else {
      const values: Omit<
        CustomFieldFilterValues<Exclude<CustomFieldOperatorType, "BTWN">>,
        "id"
      > = {
        operator: {
          type: selectedValue?.type ?? "IN",
          operands: {
            type: "DEPRECATED_ABSOLUTE",
            value: selectedValue?.value as any,
          },
        },
        include_unassigned: selectedValue?.include_unassigned ?? false,
      };
      onSubmit(values);
    }
  }

  const Unassigned = (
    <Radio
      checked={selectedValue?.include_unassigned}
      onChange={handleUnassignedChange}
    >
      <TypographyText>{t.unassigned}</TypographyText>
    </Radio>
  );

  const CustomRanges = (
    <>
      <Radio<CustomFieldOperatorType>
        value="BTWN"
        checked={selectedValue?.type === "BTWN"}
        onChange={() => {
          setSelectedValue((pre) => {
            return {
              type: "BTWN",
              include_unassigned: false,
              value: { from: "1", to: "2" },
            };
          });
        }}
      >
        <TypographyText>{t.Between}</TypographyText>
      </Radio>
      {selectedValue?.type === "BTWN" && (
        <NumericCustomRange
          value={selectedValue?.value as CustomFiledBetweenOperands["value"]}
          allowDecimal={allowDecimal}
          onChange={handleCustomRangeChange}
        />
      )}
    </>
  );

  const Footer = (
    <FilterFooter
      onCancel={onCancel}
      onSubmit={handleSubmit}
      disable={isDisable}
    />
  );

  const Content = (
    <Flex vertical>
      {Unassigned}
      <NumericSingleValues
        value={selectedValue}
        allowDecimal={allowDecimal}
        onErrorCallBack={(value) => {
          setIsDisable(value);
        }}
        onChange={(value) => {
          setSelectedValue(value);
        }}
      />

      {CustomRanges}
    </Flex>
  );
  return (
    <Flex vertical>
      {Content}
      {Footer}
    </Flex>
  );
}

export { NumericCustomField };
